import type { ItemPhase, ItemVersion } from "./types.js";
import type { Post, PostPhase, Integration } from "./index.js";

export type ArgumentType = "integration" | "user";

export interface ActionArgument {
  /** The type of entity to select from */
  type: ArgumentType;
  /** A search query to filter options (e.g., 'kind:channel enabled:true') */
  query?: string;
  /** The param key where the selected value will be merged */
  key: string;
  /** Label for the submenu/selection */
  label?: string;
}

export interface ContextualAction {
  verb: string;
  label: string;
  icon?: string;
  params: Record<string, unknown>;
  variant?: "default" | "destructive";
  argument?: ActionArgument;
}

export function getContextualActionsForItem(
  item: ItemVersion,
): ContextualAction[] {
  const actions: ContextualAction[] = [];
  const { phase, slug } = item;

  // Create post action (only for backlog items) - top priority when available
  if (phase === "backlog") {
    actions.push({
      verb: "createPost",
      label: "Create post",
      params: {
        itemSlug: slug,
        itemVersion: item.version,
      },
      argument: {
        type: "integration",
        query: "kind:channel enabled:true",
        key: "channelSlug",
        label: "Select channel",
      },
    });
  }

  // Phase transitions - logical workflow: inbox → editing → signoff → backlog
  const transitions: Record<
    ItemPhase,
    { forward?: ItemPhase; back?: ItemPhase }
  > = {
    inbox: { forward: "editing" },
    editing: { forward: "signoff", back: "inbox" },
    signoff: { forward: "backlog", back: "editing" },
    backlog: { back: "signoff" },
  };

  const t = transitions[phase] ?? {};
  if (t.forward) {
    actions.push({
      verb: "moveItem",
      label: `Move to ${t.forward}`,
      params: { itemSlug: slug, targetPhase: t.forward },
    });
  }
  if (t.back) {
    actions.push({
      verb: "moveItem",
      label: `Move to ${t.back}`,
      params: { itemSlug: slug, targetPhase: t.back },
    });
  }

  actions.push({
    verb: "moveItem",
    label: "Archive item",
    params: { itemSlug: slug, targetPhase: "archive" },
  });

  //actions.push({
  //  verb: "deleteItem",
  //  label: "Delete item",
  //  params: { itemSlug: slug },
  //  variant: "destructive",
  //});

  return actions;
}

export function getContextualActionsForPost(post: Post): ContextualAction[] {
  const actions: ContextualAction[] = [];
  const { slug, phase } = post;

  // Publish now action - schedules for 10 seconds from now
  if (phase === "scheduling") {
    actions.push({
      verb: "publishNow",
      label: "Publish now",
      params: { postSlug: slug },
    });
  }

  // Phase transitions: scheduling → outbox → published/failed
  const transitions: Record<
    PostPhase,
    { forward?: PostPhase; back?: PostPhase }
  > = {
    scheduling: { forward: "outbox" },
    outbox: { back: "scheduling" },
    published: {}, // Terminal state
    failed: { back: "scheduling" }, // Allow retry by moving back to scheduling
  };

  const t = transitions[phase] ?? {};
  if (t.forward) {
    actions.push({
      verb: "movePost",
      label: `Move to ${t.forward}`,
      params: { postSlug: slug, targetPhase: t.forward },
    });
  }
  if (t.back) {
    actions.push({
      verb: "movePost",
      label: `Move to ${t.back}`,
      params: { postSlug: slug, targetPhase: t.back },
    });
  }

  actions.push({
    verb: "movePost",
    label: "Archive post",
    params: { postSlug: slug, targetPhase: "archive" },
  });

  //actions.push({
  //  verb: "deletePost",
  //  label: "Delete post",
  //  params: { postSlug: slug },
  //  variant: "destructive",
  //});

  return actions;
}

export function getContextualActionsForIntegration(
  integration: Integration,
): ContextualAction[] {
  const actions: ContextualAction[] = [];
  const { slug, enabled } = integration;

  // Enable/disable action based on current state
  if (enabled) {
    actions.push({
      verb: "disableIntegration",
      label: "Disable integration",
      params: { integrationSlug: slug },
    });
  } else {
    actions.push({
      verb: "enableIntegration",
      label: "Enable integration",
      params: { integrationSlug: slug },
    });
  }

  // Delete action (always available)
  actions.push({
    verb: "deleteIntegration",
    label: "Delete integration",
    params: { integrationSlug: slug },
    variant: "destructive",
  });

  return actions;
}
